import { Text } from '@growtherapy/sprout-ui';

import { IntakeFormAnswer } from './IntakeFormAnswer';
import {
  INTAKE_ANSWER_TEXT_CLASSNAME,
  INTAKE_UNANSWERED_FALLBACK,
  IntakeFormValues,
  formatOptionAsQuestion,
  getConditionalCheckboxOptionFollowUps,
  getSelectedValues,
  isOptionalIntakeQuestion,
  shouldShowUncheckedOptionsInReadOnly,
  toGroupedResponse,
} from './intakeFormAnswerUtils';
import { GroupedResponse } from './types';

type IntakeFormConditionalCheckboxGroupAnswerProps = {
  questionWithAnswer: GroupedResponse;
  formValues: IntakeFormValues;
};

export function IntakeFormConditionalCheckboxGroupAnswer({
  questionWithAnswer,
  formValues,
}: IntakeFormConditionalCheckboxGroupAnswerProps) {
  const element = questionWithAnswer.questionElement;
  const options = element?.options ?? [];
  const groupName = questionWithAnswer.questionName;
  const selectedValues = getSelectedValues(questionWithAnswer.answer);
  const showUnchecked = shouldShowUncheckedOptionsInReadOnly(groupName);

  const optionRows = options.flatMap((option) => {
    const isSelected = selectedValues.includes(option.value);

    if (!isSelected && !showUnchecked) {
      return [];
    }

    const optionQuestionLabel = formatOptionAsQuestion(option.label);

    if (!isSelected) {
      return [
        <div key={`${groupName}-${option.value}`} className='flex flex-row justify-between gap-4 py-1'>
          <Text className='font-medium rebrand:text-text-inverse'>{optionQuestionLabel}</Text>
          <Text className={INTAKE_ANSWER_TEXT_CLASSNAME}>No</Text>
        </div>,
      ];
    }

    const { displayValue, followUps } = getConditionalCheckboxOptionFollowUps(option, formValues);

    return [
      <div key={`${groupName}-${option.value}`} className='flex flex-col gap-2'>
        <div className='flex flex-row justify-between gap-4 py-1'>
          <Text className='font-medium rebrand:text-text-inverse'>{optionQuestionLabel}</Text>
          <Text className={INTAKE_ANSWER_TEXT_CLASSNAME}>{displayValue}</Text>
        </div>
        {followUps.map(({ key, presentation }) => {
          switch (presentation.kind) {
            case 'none':
              return null;
            case 'value-row':
              return (
                <div key={key} className='ml-4 py-1'>
                  <Text className={INTAKE_ANSWER_TEXT_CLASSNAME}>{presentation.value}</Text>
                </div>
              );
            case 'labeled-row':
              return (
                <div key={key} className='ml-4 flex flex-row justify-between gap-4 py-1'>
                  <Text className='font-medium rebrand:text-text-inverse'>{presentation.label}</Text>
                  <Text className={INTAKE_ANSWER_TEXT_CLASSNAME}>{presentation.value}</Text>
                </div>
              );
            case 'nested-group':
              return (
                <div key={key} className='ml-4 flex flex-col gap-2'>
                  <IntakeFormConditionalCheckboxGroupAnswer
                    formValues={formValues}
                    questionWithAnswer={toGroupedResponse(presentation.element, formValues)}
                  />
                </div>
              );
            case 'complex':
              return (
                <div key={key} className='ml-4 flex flex-col gap-1 py-1'>
                  {presentation.label && (
                    <Text className='font-medium rebrand:text-text-inverse'>{presentation.label}</Text>
                  )}
                  <IntakeFormAnswer formValues={formValues} questionWithAnswer={presentation.groupedResponse} />
                </div>
              );
          }
        })}
      </div>,
    ];
  });

  if (optionRows.length === 0) {
    return isOptionalIntakeQuestion(questionWithAnswer) ? (
      <Text className={INTAKE_ANSWER_TEXT_CLASSNAME}>{INTAKE_UNANSWERED_FALLBACK}</Text>
    ) : null;
  }

  return <div className='flex flex-col gap-3'>{optionRows}</div>;
}
